import { useCallback } from "react";
import { Task } from "@common/types/task";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useAddPlaidAccountsMutation } from "@mutations/plaidConnection";
import { useTaskPlaidConnection } from "../shared/useTaskPlaidConnection";
import { getConnectionMetadata } from "../TaskBody.lib";

interface Props {
    selectedTask: Task;
}

const TaskPlaidNewAccountsAction = ({ selectedTask }: Props) => {
    const { connectionId, isIntegrationConnectionId } =
        getConnectionMetadata(selectedTask);
    const { mutate: addAccountsMutate, isPending } =
        useAddPlaidAccountsMutation();
    const { plaidConnection, isLoading } = useTaskPlaidConnection(
        isIntegrationConnectionId ? null : connectionId,
    );
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const addAccountTooltip = useViewOnlyTooltipReason(
        "Only connection owner can add another account",
    );
    const viewOnlyTooltip = useViewOnlyTooltipReason("");

    const handleAddAccounts = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        if (plaidConnection) {
            addAccountsMutate(plaidConnection);
        }
    }, [refuseIfViewOnly, addAccountsMutate, plaidConnection]);

    if (isLoading) {
        return null;
    }

    if (!connectionId || isIntegrationConnectionId) {
        return null;
    }

    if (!plaidConnection) {
        return (
            <Tooltip content={addAccountTooltip}>
                <Button disabled variant="primary" size="md">
                    Add another account
                </Button>
            </Tooltip>
        );
    }

    return (
        <Tooltip content={viewOnlyTooltip}>
            <ButtonWithLoader
                loading={isPending}
                variant="primary"
                size="md"
                onClick={handleAddAccounts}
                disabled={isViewOnly}
            >
                Add another account
            </ButtonWithLoader>
        </Tooltip>
    );
};

export const PlaidNewAccountsCommandIsland = ({ selectedTask }: Props) => (
    <CommandIsland alignEnd className="mx-3" sticky>
        <TaskPlaidNewAccountsAction selectedTask={selectedTask} />
    </CommandIsland>
);
